import { Injectable, inject } from '@angular/core';
import { SessionStorageService } from '@df/browser/storage/session-storage.service';
import { App } from '@df/core/app';
import { Subject } from 'rxjs/internal/Subject';
import { strict as strictUtm } from '../../module/gtm/utm';

/**
 * Service for handling advertising and campaign traffic parameters.
 * Centralizes detection of UTM parameters and various advertising platform tracking codes.
 * Manages session flags to control multisite behavior for advertising traffic.
 */
@Injectable({
    providedIn: 'root'
})
export class AdvertisingTrafficService {
    private sessionStorageService = inject(SessionStorageService);
    private app = inject(App);

    // Subject for emitting when initial ad traffic handling changes
    private initialAdTrafficHandledSubject = new Subject<boolean>();

    // Public observable for listening to initial ad traffic handling changes
    readonly initialAdTrafficHandled$ = this.initialAdTrafficHandledSubject.asObservable();

    // Performance optimizations: caches
    private storageCache = new Map<string, string | boolean | Record<string, string> | null>();
    private cachedUrlParams: URLSearchParams | null = null;
    private cachedUtmParams: Record<string, string> | null = null;

    // All advertising platform parameters consolidated - using Set for O(1) lookup
    private readonly AD_PARAMS = new Set([
        'gclid', 'gbraid', 'gad_source', 'gad_campaignid', // Google
        'fbclid', 'fb_source', 'fb_ref', // Facebook
        'msclkid', // Microsoft
        'ttclid', // TikTok
        'twclid', // Twitter
        'li_fat_id' // LinkedIn
    ]);

    // Session storage keys
    private readonly ADVERTISING_SESSION_KEY = 'advertising_traffic_session';
    private readonly INITIAL_HANDLED_KEY = 'initial_ad_traffic_handled';
    private readonly ORIGINAL_URL_KEY = 'advertising_traffic_original_url';
    private readonly UTM_PARAMS_KEY = 'utm_params';

    /**
     * Gets a value from session storage with proper type handling and caching
     */
    private getStorageValue(key: string): string | boolean | Record<string, string> | null {
        if (!this.app.isPlatformBrowser) {
            return null;
        }
        
        // Check memory cache first
        if (this.storageCache.has(key)) {
            return this.storageCache.get(key) ?? null;
        }
        
        const value = this.sessionStorageService.get(key);
        if (value === null || value === undefined) {
            this.storageCache.set(key, null);
            return null;
        }
        
        try {
            const parsed = JSON.parse(value as string);
            this.storageCache.set(key, parsed);
            return parsed;
        } catch {
            this.storageCache.set(key, value);
            return value;
        }
    }

    /**
     * Sets a value in session storage with proper type handling and cache update
     */
    private setStorageValue(key: string, value: string | boolean | Record<string, string>): void {
        if (!this.app.isPlatformBrowser) {
            return;
        }
        this.sessionStorageService.set(key, JSON.stringify(value));
        // Update cache
        this.storageCache.set(key, value);
    }

    /**
     * Gets cached URL parameters
     */
    private getUrlParams(): URLSearchParams {
        if (!this.cachedUrlParams) {
            this.cachedUrlParams = new URLSearchParams(window.location.search);
        }
        return this.cachedUrlParams;
    }

    /**
     * Gets cached UTM parameters
     */
    private getUtmParams(): Record<string, string> {
        if (!this.cachedUtmParams) {
            this.cachedUtmParams = strictUtm(window.location.href);
        }
        return this.cachedUtmParams;
    }

    /**
     * Checks if the current URL contains any advertising/campaign tracking parameters
     */
    hasAdvertisingTrafficParameters(): boolean {
        if (!this.app.isPlatformBrowser) {
            return false;
        }

        const urlParams = this.getUrlParams();
        const utmParams = this.getUtmParams();
        
        // Check UTM params
        if (Object.keys(utmParams).length > 0) {
            return true;
        }
        
        // Check ad params - using Set.has() for O(1) lookup
        for (const param of this.AD_PARAMS) {
            if (urlParams.has(param)) {
                return true;
            }
        }
        
        return false;
    }

    /**
     * Checks if this is initial advertising traffic that hasn't been handled yet
     */
    shouldSkipMultisiteForInitialAdTraffic(): boolean {
        if (!this.app.isPlatformBrowser) {
            return false;
        }

        // Check if initial ad traffic has already been handled
        if (this.isInitialAdTrafficHandled()) {
            return false;
        }

        // Check if we have advertising traffic parameters
        return this.hasAdvertisingTrafficParameters();
    }

    /**
     * Checks if initial advertising traffic has been handled (after first navigation)
     */
    isInitialAdTrafficHandled(): boolean {
        return this.getStorageValue(this.INITIAL_HANDLED_KEY) === true;
    }

    /**
     * Marks that initial advertising traffic has been handled
     * Should be called after first internal navigation
     */
    markInitialAdTrafficAsHandled(): void {
        this.setStorageValue(this.INITIAL_HANDLED_KEY, true);
        this.initialAdTrafficHandledSubject.next(true);
    }

    /**
     * Checks if current session came from advertising traffic
     */
    isAdvertisingSession(): boolean {
        return this.getStorageValue(this.ADVERTISING_SESSION_KEY) === true;
    }

    /**
     * Stores advertising/campaign traffic parameters in sessionStorage
     * Preserves them for tracking and analytics purposes
     */
    storeAdvertisingParameters(): void {
        if (!this.app.isPlatformBrowser) {
            return;
        }

        const urlParams = this.getUrlParams();
        const utmParams = this.getUtmParams();
        const hasUtmParams = Object.keys(utmParams).length > 0;
        
        // Check for ad params using Set iteration
        let hasAdParams = false;
        for (const param of this.AD_PARAMS) {
            if (urlParams.has(param)) {
                hasAdParams = true;
                break;
            }
        }

        if (hasUtmParams || hasAdParams) {
            this.setStorageValue(this.ADVERTISING_SESSION_KEY, true);
            this.setStorageValue(this.ORIGINAL_URL_KEY, window.location.pathname + window.location.search);

            if (hasUtmParams) {
                this.setStorageValue(this.UTM_PARAMS_KEY, utmParams);
            }

            // Store individual ad parameters
            for (const param of this.AD_PARAMS) {
                const value = urlParams.get(param);
                if (value) {
                    this.setStorageValue(`ad_param_${param}`, value);
                }
            }
        }
    }

    /**
     * Gets stored UTM parameters if any
     */
    getStoredUtmParams(): Record<string, string> | null {
        const value = this.getStorageValue(this.UTM_PARAMS_KEY);
        return value && typeof value === 'object' && !Array.isArray(value) 
            ? value as Record<string, string> 
            : null;
    }

    /**
     * Gets the original URL that brought the user to the site
     */
    getOriginalUrl(): string | null {
        const value = this.getStorageValue(this.ORIGINAL_URL_KEY);
        return typeof value === 'string' ? value : null;
    }
}
